// Die gesendeten Besichtigungen (E3, S2; Mockup /crm/pruefen – bis E3c
// „Zu prüfen“; seit S2 steht, was zu tun ist, unter „Zu tun“, und diese
// Seite ist die Kiste Sales/Doku): jede gesendete Besichtigung, geordnet vom
// Server – zuerst, was zu prüfen ist (neueste zuerst), dann „bitte ansehen“,
// dann die freigegebenen. Je Zeile: Kunde, Adresse,
// Verkäufer, wann gesendet, wie viele Stationen fertig sind (offene
// deutlich), die Dachplanung, offene Fragen, der Stand. Ein Klick auf die
// Zeile öffnet die Freigabe; mit der Tastatur ist der Name ein Link.
//
// Der Verkauf sieht hier nur seine eigenen (das regelt der Server) – die
// Seite heißt dann „Meine Besichtigungen“, ohne Spalte Verkäufer, und der
// Stand sagt „beim Büro“ statt „wartet auf Freigabe“.

import type { K2Stand, PruefListeAntwort, PruefZeile } from "@happy-energy/kern";
import { Chip, Spaeter, Tipp } from "@happy-energy/oberflaeche";
import { kuerzel, standArt, wannZeigen } from "./anzeige.ts";
import { StandChip } from "./bausteine.tsx";
import { Laedt, LadeFehler, useLaden } from "./laden.tsx";
import { nurEigene } from "./navigation.ts";
import { geheZu, Seite } from "./rahmen.tsx";
import { useCrm } from "./zusammenhang.tsx";

function K2Chip({ k2 }: { k2: K2Stand }) {
  if (k2 === "abgelegt")
    return (
      <Chip art="ok" ikon="haken">
        abgelegt
      </Chip>
    );
  if (k2 === "uebersprungen") return <Chip>übersprungen</Chip>;
  return <Chip ikon="uhr">offen</Chip>;
}

/** „Frau Maierhofer, Herr Gruber“ – höchstens drei Namen, dann „und 2 weitere“. */
function namen(zeilen: readonly PruefZeile[]): string {
  const n = zeilen.map((z) => z.kundeName);
  return n.length <= 3 ? n.join(", ") : `${n.slice(0, 3).join(", ")} und ${n.length - 3} weitere`;
}

export function Pruefen() {
  const { ich } = useCrm();
  const eigene = nurEigene(ich.nutzer.rolle);
  const { daten, fehler, neu } = useLaden<PruefListeAntwort>("/api/crm/aufnahmen");
  const liste = daten?.aufnahmen ?? [];
  const neue = liste.filter((z) => z.hinweis === "neu" || z.hinweis === "nachtrag");
  const ansehen = liste.filter((z) => z.hinweis === "bitte-ansehen");

  return (
    <Seite
      titel={eigene ? "Meine Besichtigungen" : "Besichtigungen"}
      unter={eigene ? "Was du an das Büro gesendet hast – und wie weit es ist" : "Alle gesendeten Besichtigungen aus Happy Energy Doku – was jetzt zu tun ist, steht unter „Zu tun“"}
    >
      {fehler !== undefined && <LadeFehler fehler={fehler} nochmal={neu} />}
      {!daten && fehler === undefined && <Laedt />}
      {daten && liste.length === 0 && (
        <Tipp>
          {eigene
            ? "Noch keine Besichtigung gesendet – sie erscheint hier, sobald du in der Doku-App „An das Büro senden“ tippst."
            : "Noch keine Besichtigung gesendet – sie erscheint hier, sobald der Verkauf „An das Büro senden“ tippt."}
        </Tipp>
      )}
      {!eigene && neue.length > 0 && (
        <Tipp art="gelb" ikon="schild">
          Ein Klick auf eine Zeile öffnet die Besichtigung. <b>{namen(neue)}</b> {neue.length === 1 ? "wartet" : "warten"} auf die Freigabe.
        </Tipp>
      )}
      {!eigene && ansehen.length > 0 && (
        <Tipp art="warn" ikon="auge">
          Bei <b>{namen(ansehen)}</b> kam nach der Freigabe etwas dazu – bitte ansehen.
        </Tipp>
      )}
      {liste.length > 0 && (
        <div className="tabelle-wrap">
          <table className="tabelle">
            <thead>
              <tr>
                <th>Kunde</th>
                <th>Adresse</th>
                {!eigene && <th>Verkäufer</th>}
                <th>Gesendet</th>
                <th>Stationen</th>
                <th>K2-PDF</th>
                <th>Fragen</th>
                <th>Stand</th>
              </tr>
            </thead>
            <tbody>
              {liste.map((z) => {
                const offen = z.stationenFertig < z.stationenGesamt;
                const stand = standArt(z, !eigene);
                return (
                  <tr key={z.aufnahmeId} className={`klickbar${z.hinweis ? ` hinweis-${z.hinweis}` : ""}`} onClick={() => geheZu(`/freigabe/${z.aufnahmeId}`)}>
                    <td>
                      <div className="tabelle-kunde">
                        <span className={`avatar${stand === "neu" || stand === "nachtrag" ? " avatar-lime" : ""}`}>{kuerzel(z.kundeName)}</span>
                        <a href={`#/freigabe/${z.aufnahmeId}`} onClick={(e) => e.stopPropagation()}>
                          {z.kundeName}
                        </a>
                      </div>
                    </td>
                    <td>
                      {z.strasse}
                      <span className="tabelle-unter">
                        {z.plz} {z.ort}
                      </span>
                    </td>
                    {!eigene && <td>{z.verkaeufer.name}</td>}
                    <td className="nicht-umbrechen">{wannZeigen(z.gesendetAm)}</td>
                    <td className="nicht-umbrechen">
                      {offen ? (
                        <Chip art="warn" ikon="achtung">
                          {z.stationenFertig} von {z.stationenGesamt}
                        </Chip>
                      ) : (
                        <span>
                          {z.stationenFertig} von {z.stationenGesamt}
                        </span>
                      )}
                    </td>
                    <td>
                      <K2Chip k2={z.k2} />
                    </td>
                    <td>
                      {z.offeneFragen > 0 ? (
                        <Chip art="frage" ikon="frage">
                          {z.offeneFragen} offen
                        </Chip>
                      ) : (
                        <span className="leise">–</span>
                      )}
                    </td>
                    <td>
                      <StandChip stand={stand} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {!eigene && (
        <Spaeter titel="Filtern, suchen, Nachricht bei Neuem" wann="Stufe 2">
          Nach Verkäufer, Ort oder Stand filtern; das Büro bekommt eine Nachricht, sobald etwas hereinkommt.
        </Spaeter>
      )}
    </Seite>
  );
}
